<div
  class="ag1-table--container"
  [class.border]="hasBorder"
  [class.shadow-sm]="hasBorder"
  [class.rounded-lg]="isRounded"
>
  <!-- table -->
  @if (!isLoading && data?.virtualRows?.length) {
    <ng-container *ngTemplateOutlet="tableTemplate" />
  }

  <!-- empty -->
  @if (!isLoading && !data?.virtualRows?.length) {
    <ng-container [ngTemplateOutlet]="emptyTemplate" />
  }

  <!-- loading -->
  @if (isLoading && hasLoadingAnimation) {
    <ng-container [ngTemplateOutlet]="loadingTemplate" />
  }
</div>

<ng-template #tableTemplate>
  <div #table class="ag1-table--table">
    <!-- header -->
    <div
      #header
      class="ag1-table--header"
      tableScrollSync
      [scrollWatch]="bodyContainer"
    >
      <!--
      CDK Drop List for Column Reordering.
      - cdkDropList: Defines this container as a drop zone.
      - [cdkDropListData]: The data array associated with the list.
      - cdkDropListOrientation: Set to horizontal for columns.
      - (cdkDropListDropped): The event that fires when an item is dropped.
      - [cdkDropListConnectedTo]: Connects to external drop zones.
      -->
      <div
        class="ag1-table--header-row"
        cdkDropList
        [cdkDropListData]="checkedFields"
        cdkDropListOrientation="horizontal"
        (cdkDropListDropped)="onColumnDrop($event)"
        [cdkDropListConnectedTo]="connectedDropLists"
      >
        <!-- header actions -->
        @if (
          hasNestedRow || hasCustomAction || hasGroupedData || hasSelectableData
        ) {
          <div
            class="ag1-table--header-action-cell"
            [style.width]="getActionWidth()"
            #headerActionCell
          >
            <!-- select all action -->
            <ng-container [ngTemplateOutlet]="headerSelectTemplate" />
            <!-- group all action-->
            <ng-container [ngTemplateOutlet]="headerGroupTemplate" />
            <!-- actions -->
            <ng-container
              [ngTemplateOutlet]="
                customHeaderActionRef || defaultHeaderActionTemplate
              "
            />
          </div>
        }

        <!-- bulk actions -->
        @if (
          (isAnyRowSelected || isAllRowsSelected) && customHeaderBulkActionRef
        ) {
          <ng-container [ngTemplateOutlet]="headerBulkActionsTemplate" />
        }

        <!-- header fields (only checked/visible columns, same as body) -->
        @if (
          !(
            (isAnyRowSelected || isAllRowsSelected) &&
            customHeaderBulkActionRef
          )
        ) {
          @for (
            field of checkedFields;
            track tableService.trackByField(index, field);
            let index = $index
          ) {
            <!--
              CDK Drag Item for each column.
              - cdkDrag: Makes the element draggable.
              - [cdkDragData]: The data associated with the draggable item.
              - [cdkDragDisabled]: Disables dragging based on a condition.
              -->
            <div
              cdkDrag
              [cdkDragData]="field"
              [cdkDragDisabled]="isDraggingDisabled"
              class="ag1-table--header-cell group relative"
              [style.width]="getColumnWidth(field)"
            >
              <!-- move handle -->
              <ng-template [ngTemplateOutlet]="headerMoveHandleTemplate" />
              <div class="flex items-center gap-1">
                <!-- field name -->
                <ng-container
                  [ngTemplateOutlet]="
                    customHeaderCellRef || defaultHeaderCellTemplate
                  "
                  [ngTemplateOutletContext]="{ field: field }"
                />
                <!-- sorting -->
                <ng-container
                  [ngTemplateOutlet]="headerSortTemplate"
                  [ngTemplateOutletContext]="{ field: field }"
                />
              </div>
              <!-- field resize -->
              <ng-container
                [ngTemplateOutlet]="headerResizeGripTemplate"
                [ngTemplateOutletContext]="{ field: field }"
              />
              <!-- Custom placeholder that shows while dragging -->
              <div
                *cdkDragPlaceholder
                class="ag1-table--header-cell-placeholder"
                [style.width]="getColumnWidth(field)"
              >
                <div class="pl-3">
                  <span class="min-w-0 truncate">
                    @if (field?.displayName) {
                      {{ field?.displayName }}
                    } @else {
                      {{ field.name | humanize | sentenceCase }}
                    }
                  </span>
                </div>
              </div>
            </div>
          }
        }
      </div>
    </div>

    <!-- body -->
    <cdk-virtual-scroll-viewport
      class="ag1-table--body"
      [itemSize]="rowHeight"
      #bodyContainer
      [style.height]="viewportHeight"
    >
      <ng-container
        *cdkVirtualFor="
          let item of data.virtualRows;
          let rowIndex = index;
          let count = count;
          let first = first;
          let last = last;
          let even = even;
          let odd = odd;
          trackBy: trackByVirtualRow
        "
      >
        <!-- group row -->
        @if (isGroupRow(item)) {
          <div class="ag1-table--group-row">
            <div class="ag1-table--group-cell">
              <!-- group selection action -->
              <ng-container
                [ngTemplateOutlet]="bodyGroupSelectionTemplate"
                [ngTemplateOutletContext]="{
                  group: item,
                }"
              />
              <!-- group expansion action -->
              <ng-container
                [ngTemplateOutlet]="bodyGroupActionTemplate"
                [ngTemplateOutletContext]="{
                  group: item,
                }"
              />
              @for (
                groupedField of groupedFields;
                track tableService.trackByField($index, groupedField);
                let last = $last
              ) {
                <!-- group by field value -->
                <ng-container
                  [ngTemplateOutlet]="
                    customBodyCellRef || defaultBodyCellTemplate
                  "
                  [ngTemplateOutletContext]="{
                    item: item.groupRows[0],
                    field: groupedField,
                  }"
                />
                <!-- separator -->
                @if (!last) {
                  <svg-icon
                    class="text-slate-400"
                    src="/assets/icons/general/chevron-right.svg"
                  />
                }
              }
              <!-- group count -->
              <div
                class="ml-2 flex items-center gap-3 text-sm font-normal text-slate-500"
              >
                <span>|</span>
                <span>
                  {{ item.count }}
                  <ng-container [ngPlural]="item.count">
                    <ng-template ngPluralCase="=1">item</ng-template>
                    <ng-template ngPluralCase="other">items</ng-template>
                  </ng-container>
                </span>
              </div>
            </div>
          </div>
        }

        <!-- normal row -->
        @if (isNormalRow(item)) {
          <div
            class="ag1-table--body-row group"
            [class.bg-white]="even"
            [class.bg-slate-50]="!even"
            [class.ag1-table--body-row-selected]="selectedRows.has(item.id)"
            [class.ag1-table--body-row-flashed]="flashedRows.has(item.id)"
          >
            <!-- body actions -->
            @if (
              hasNestedRow ||
              hasCustomAction ||
              hasGroupedData ||
              hasSelectableData
            ) {
              <div
                #bodyActionCell
                class="ag1-table--body-action-cell"
                [style.width]="getActionWidth()"
                [class.bg-white]="even"
                [class.bg-slate-50]="!even"
                [class.ag1-table--body-row-selected]="selectedRows.has(item.id)"
                [class.ag1-table--body-row-flashed]="flashedRows.has(item.id)"
              >
                <!-- row selection -->
                <ng-container
                  [ngTemplateOutlet]="bodyRowSelectionTemplate"
                  [ngTemplateOutletContext]="{
                    item: item,
                  }"
                />
                <!-- row expand -->
                <ng-container
                  [ngTemplateOutlet]="bodyRowExpansionTemplate"
                  [ngTemplateOutletContext]="{
                    rowIndex: rowIndex,
                    item: item,
                    groupHeader: groupHeader,
                  }"
                />
                <!-- actions -->
                <ng-container
                  [ngTemplateOutlet]="
                    customBodyActionRef || defaultBodyActionTemplate
                  "
                  [ngTemplateOutletContext]="{
                    rowIndex: rowIndex,
                    item: item,
                  }"
                />
              </div>
            }
            <!-- body cells -->
            @for (
              field of checkedFields;
              track tableService.trackByField($index, field)
            ) {
              <div
                class="ag1-table--body-cell relative"
                [style.width]="getColumnWidth(field)"
                [class]="
                  columnResizeState.isResizing &&
                  columnResizeState.columnName === field.name
                    ? 'after:absolute after:inset-y-0 after:right-0 after:block after:w-1 after:bg-steel-blue-500'
                    : ''
                "
                [class.bg-white]="even"
                [class.bg-slate-50]="!even"
                [class.ag1-table--body-row-selected]="selectedRows.has(item.id)"
                [class.ag1-table--body-row-flashed]="flashedRows.has(item.id)"
              >
                <div class="w-full truncate">
                  <ng-container
                    [ngTemplateOutlet]="
                      customBodyCellRef || defaultBodyCellTemplate
                    "
                    [ngTemplateOutletContext]="{
                      item: item,
                      field: field,
                      rowIndex: rowIndex,
                    }"
                  />
                </div>
              </div>
            }
          </div>
        }

        <!-- nested row -->
        @if (rowIndex === rowExpansionState.expandedRowIndex && hasNestedRow) {
          <ng-container
            [ngTemplateOutlet]="bodyNestedRowTemplate"
            [ngTemplateOutletContext]="{ item: item, rowIndex: rowIndex }"
          />
        }
      </ng-container>
    </cdk-virtual-scroll-viewport>
  </div>
</ng-template>

<!-- header templates -->
<ng-template #defaultHeaderCellTemplate let-field="field">
  <span>
    @if (field?.displayName) {
      {{ field?.displayName }}
    } @else {
      {{ field.name | humanize | sentenceCase }}
    }
  </span>
</ng-template>

<ng-template #headerSortTemplate let-field="field">
  @if (!field?.sortDirection && field?.sortable) {
    <p-button
      size="small"
      severity="contrast"
      variant="text"
      class="opacity-0 group-hover:opacity-100"
      [pTooltip]="
        field?.displayName
          ? 'Sort: ' + $safeNavigationMigration(field?.displayName)
          : 'Sort: ' + (field.name | humanize | sentenceCase)
      "
      tooltipPosition="right"
      (click)="updateColumnSortDirection(field)"
    >
      <ng-template #icon>
        <svg-icon src="/assets/icons/general/sort-asc.svg" />
      </ng-template>
    </p-button>
  }

  @if (field?.sortDirection && field?.sortable) {
    <p-button
      size="small"
      severity="contrast"
      variant="text"
      [pTooltip]="
        field?.displayName
          ? 'Sort: ' + $safeNavigationMigration(field?.displayName)
          : 'Sort: ' + (field.name | humanize | sentenceCase)
      "
      tooltipPosition="right"
      (click)="updateColumnSortDirection(field)"
    >
      <ng-template #icon>
        @if (field?.sortDirection === "asc") {
          <svg-icon src="/assets/icons/general/sort-asc.svg" />
        }
        @if (field?.sortDirection === "desc") {
          <svg-icon src="/assets/icons/general/sort-desc.svg" />
        }
      </ng-template>
    </p-button>
  }
</ng-template>

<ng-template #headerResizeGripTemplate let-field="field">
  <div
    class="absolute inset-y-0 right-0 cursor-ew-resize group-last-of-type:hidden"
  >
    <!-- line -->
    <div
      [class]="
        columnResizeState.isResizing &&
        columnResizeState.columnName === field.name
          ? 'w-1 bg-steel-blue-500'
          : 'w-0.5 bg-transparent'
      "
      class="absolute inset-y-0 right-0 transition-all hover:w-1 hover:bg-steel-blue-500"
      [pTooltip]="
        field?.displayName
          ? 'Resize: ' + $safeNavigationMigration(field?.displayName)
          : 'Resize: ' + (field.name | humanize | sentenceCase)
      "
      (mousedown)="onColumnResizeMouseDown($event, field)"
    ></div>
  </div>
</ng-template>

<!-- header action templates -->
<ng-template #headerBulkActionsTemplate>
  <div class="ag1-table--header-bulk-action-container">
    <ng-container [ngTemplateOutlet]="customHeaderBulkActionRef" />
  </div>
</ng-template>

<ng-template #headerSelectTemplate>
  @if (hasSelectableData) {
    <input
      type="checkbox"
      [class.indeterminate]="isAnyRowSelected"
      [checked]="isAllRowsSelected"
      (change)="toggleSelectAllRows()"
    />
  }
</ng-template>

<ng-template #headerGroupTemplate>
  @if (hasGroupedData) {
    @if (!isAllGroupsExpanded) {
      <p-button
        severity="contrast"
        variant="text"
        size="small"
        pTooltip="Expand All"
        tooltipPosition="right"
        (click)="expandAllGroups()"
      >
        <ng-template #icon>
          <svg-icon src="/assets/icons/general/chevron-right.svg" />
        </ng-template>
      </p-button>
    }
    @if (isAllGroupsExpanded) {
      <p-button
        severity="contrast"
        variant="text"
        size="small"
        pTooltip="Collapse All"
        tooltipPosition="right"
        (click)="collapseAllGroups()"
      >
        <ng-template #icon>
          <svg-icon src="/assets/icons/general/chevron-down.svg" />
        </ng-template>
      </p-button>
    }
  }
</ng-template>

<ng-template #defaultHeaderActionTemplate>
  <!-- Placeholder for header actions -->
</ng-template>

<!-- body templates -->
<ng-template #defaultBodyCellTemplate let-item="item" let-field="field">
  {{ item[field.name] }}
</ng-template>

<ng-template #headerMoveHandleTemplate>
  <svg-icon
    pTooltip="Drag to re-order"
    class="h-4 w-4 -translate-x-1 cursor-move text-slate-500 opacity-0 group-hover:opacity-100"
    src="/assets/icons/general/drag.svg"
  />
</ng-template>

<ng-template #bodyGroupSelectionTemplate let-group="group">
  @if (hasSelectableData) {
    <input
      type="checkbox"
      [class.indeterminate]="isAnyGroupRowSelected(group)"
      [checked]="isAllGroupRowsSelected(group)"
      (change)="toggleGroupSelection(group)"
    />
  }
</ng-template>

<ng-template #bodyGroupActionTemplate let-group="group">
  @if (!group.expanded) {
    <p-button
      size="small"
      severity="contrast"
      variant="text"
      pTooltip="Expand"
      tooltipPosition="right"
      (click)="expandGroup(group)"
    >
      <ng-template #icon>
        <svg-icon src="/assets/icons/general/chevron-right.svg" />
      </ng-template>
    </p-button>
  }
  @if (group.expanded) {
    <p-button
      size="small"
      severity="contrast"
      variant="text"
      pTooltip="Collapse"
      tooltipPosition="right"
      (click)="collapseGroup(group)"
    >
      <ng-template #icon>
        <svg-icon src="/assets/icons/general/chevron-down.svg" />
      </ng-template>
    </p-button>
  }
</ng-template>

<!-- body action templates -->
<ng-template #bodyRowSelectionTemplate let-item="item">
  @if (hasSelectableData) {
    @if (!excludedRowsFromSelection.includes(item.id)) {
      <input
        type="checkbox"
        [checked]="selectedRows.has(item.id)"
        (change)="toggleRowSelection(item)"
      />
    }
    @if (excludedRowsFromSelection.includes(item.id)) {
      <input type="checkbox" [disabled]="true" />
    }
  }
</ng-template>

<ng-template
  #bodyRowExpansionTemplate
  let-rowIndex="rowIndex"
  let-item="item"
  let-groupHeader="groupHeader"
>
  @if (hasNestedRow) {
    @if (
      rowExpansionState.expandedRowIndex !== rowIndex ||
      (groupHeader && groupHeader !== rowExpansionState.expandedRowGroupHeader)
    ) {
      <p-button
        size="small"
        severity="contrast"
        variant="text"
        pTooltip="Expand"
        tooltipPosition="right"
        (click)="toggleNestedRow(item, rowIndex, groupHeader)"
      >
        <ng-template #icon>
          <svg-icon src="/assets/icons/general/chevron-right.svg" />
        </ng-template>
      </p-button>
    }
    @if (
      (rowExpansionState.expandedRowIndex === rowIndex && !groupHeader) ||
      (groupHeader &&
        groupHeader === rowExpansionState.expandedRowGroupHeader &&
        rowExpansionState.expandedRowIndex === rowIndex)
    ) {
      <p-button
        size="small"
        severity="contrast"
        variant="text"
        pTooltip="Collapse"
        tooltipPosition="right"
        (click)="toggleNestedRow(item, rowIndex, groupHeader)"
      >
        <ng-template #icon>
          <svg-icon src="/assets/icons/general/chevron-down.svg" />
        </ng-template>
      </p-button>
    }
  }
</ng-template>

<ng-template #defaultBodyActionTemplate let-item="item" let-rowIndex="rowIndex">
  <!-- implement default template if needed -->
</ng-template>

<!-- body nested template -->

<ng-template #bodyNestedRowTemplate let-item="item" let-rowIndex="rowIndex">
  <div
    class="ag1-table--nested-row sticky left-0"
    [style.width.px]="parentWidth"
  >
    <!-- nested row -->
    <ng-container
      [ngTemplateOutlet]="nestedRowRef"
      [ngTemplateOutletContext]="{
        item: item,
        rowIndex: rowIndex,
      }"
    />
  </div>
</ng-template>

<!-- state templates -->
<ng-template #emptyTemplate>
  <div class="grid h-full grow place-items-center">
    <div
      class="grid h-full w-full place-items-center gap-2 rounded-xl bg-white p-5"
    >
      <div class="grid grid-cols-[2rem_1fr] place-items-center gap-3">
        <svg-icon
          class="w-fit scale-[2]"
          svgClass="text-steel-blue-500"
          src="/assets/icons/general/warning.svg"
        />
        <div class="flex flex-col gap-1">
          <h2 class="text-lg font-semibold">No data found.</h2>
          Try adjusting the filters or search criteria to view data.
        </div>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #loadingTemplate>
  <div class="grid h-full grow place-items-center">
    <div
      class="grid h-full w-full place-items-center gap-2 rounded-xl bg-white p-5"
    >
      <ag1-loader />
    </div>
  </div>
</ng-template>
